/**
 * Modifier Pool - Slot-aware modifier system
 *
 * POE-style modifier pool with:
 * - Slot/category/weapon-type restrictions
 * - Multi-tier value ranges (T1 best -> T8 worst)
 * - ilvl gating per tier
 * - Weighted random selection
 * - Mod groups to prevent duplicates
 */

import { EquipmentSlot, EquipmentItem, ModifierCalcType, ItemMod, WeaponSubType, EquipmentCategory } from '@shared/types';

// ========================
// Types
// ========================

export interface ModTierEntry {
  tier: number;         // T1 = best, T8 = worst
  ilvlRequired: number; // Minimum ilvl to roll this tier
  valueMin: number;
  valueMax: number;
  weight: number;       // Roll weight (higher = more common)
}

export interface ModDefinition {
  id: string;
  type: string;              // Stat type (maxHp, damage, etc.)
  calcType: ModifierCalcType;
  pool: 'prefix' | 'suffix';
  tiers: ModTierEntry[];
  allowedSlots?: EquipmentSlot[];         // If set, only these slots
  allowedCategories?: EquipmentCategory[]; // If set, only these categories
  allowedWeaponTypes?: WeaponSubType[];    // If set, only these weapon types
  modGroup?: string;                       // Same group mods can't coexist
  namePrefix?: string;                     // Turkish name prefix
  nameSuffix?: string;                     // Turkish name suffix
}

// ========================
// PREFIX MODS
// ========================

const PREFIX_MOD_DEFINITIONS: ModDefinition[] = [
  // --- Flat HP (all slots) ---
  {
    id: 'prefix_flat_hp',
    type: 'maxHp',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'life',
    namePrefix: 'Canlı',
    tiers: [
      { tier: 8, ilvlRequired: 1,  valueMin: 8,   valueMax: 15,  weight: 1000 },
      { tier: 7, ilvlRequired: 5,  valueMin: 16,  valueMax: 25,  weight: 900 },
      { tier: 6, ilvlRequired: 10, valueMin: 26,  valueMax: 35,  weight: 800 },
      { tier: 5, ilvlRequired: 15, valueMin: 36,  valueMax: 50,  weight: 600 },
      { tier: 4, ilvlRequired: 20, valueMin: 51,  valueMax: 70,  weight: 400 },
      { tier: 3, ilvlRequired: 25, valueMin: 71,  valueMax: 90,  weight: 200 },
      { tier: 2, ilvlRequired: 30, valueMin: 91,  valueMax: 110, weight: 100 },
      { tier: 1, ilvlRequired: 35, valueMin: 111, valueMax: 130, weight: 50 },
    ],
  },
  // --- % HP (all slots) ---
  {
    id: 'prefix_pct_hp',
    type: 'maxHp',
    calcType: 'increased',
    pool: 'prefix',
    modGroup: 'life_pct',
    namePrefix: 'Güçlü',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 3,  valueMax: 5,  weight: 800 },
      { tier: 5, ilvlRequired: 10, valueMin: 6,  valueMax: 8,  weight: 600 },
      { tier: 4, ilvlRequired: 18, valueMin: 9,  valueMax: 12, weight: 400 },
      { tier: 3, ilvlRequired: 24, valueMin: 13, valueMax: 16, weight: 200 },
      { tier: 2, ilvlRequired: 30, valueMin: 17, valueMax: 20, weight: 100 },
      { tier: 1, ilvlRequired: 36, valueMin: 21, valueMax: 25, weight: 50 },
    ],
  },
  // --- Flat Mana (all slots) ---
  {
    id: 'prefix_flat_mana',
    type: 'maxMana',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'mana',
    namePrefix: 'Ruhani',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 5,  valueMax: 12, weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 13, valueMax: 20, weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 21, valueMax: 30, weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 31, valueMax: 42, weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 43, valueMax: 55, weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 56, valueMax: 70, weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 71, valueMax: 85, weight: 40 },
    ],
  },
  // --- Flat Armour (armour category only) ---
  {
    id: 'prefix_flat_armor',
    type: 'armor',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'local_armor',
    allowedCategories: ['armour'],
    namePrefix: 'Zırhlı',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 8,  valueMax: 15,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 16, valueMax: 28,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 29, valueMax: 42,  weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 43, valueMax: 60,  weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 61, valueMax: 80,  weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 81, valueMax: 100, weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 101, valueMax: 120, weight: 40 },
    ],
  },
  // --- % Armour (armour category only) ---
  {
    id: 'prefix_pct_armor',
    type: 'armor',
    calcType: 'increased',
    pool: 'prefix',
    modGroup: 'local_armor_pct',
    allowedCategories: ['armour'],
    namePrefix: 'Korumalı',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 700 },
      { tier: 5, ilvlRequired: 12, valueMin: 11, valueMax: 18,  weight: 500 },
      { tier: 4, ilvlRequired: 20, valueMin: 19, valueMax: 28,  weight: 300 },
      { tier: 3, ilvlRequired: 26, valueMin: 29, valueMax: 38,  weight: 150 },
      { tier: 2, ilvlRequired: 32, valueMin: 39, valueMax: 50,  weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 51, valueMax: 65,  weight: 40 },
    ],
  },
  // --- Flat Evasion (armour category only) ---
  {
    id: 'prefix_flat_evasion',
    type: 'evasion',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'local_evasion',
    allowedCategories: ['armour'],
    namePrefix: 'Çevik',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 6,  valueMax: 12,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 13, valueMax: 22,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 23, valueMax: 35,  weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 36, valueMax: 50,  weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 51, valueMax: 70,  weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 71, valueMax: 90,  weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 91, valueMax: 110, weight: 40 },
    ],
  },
  // --- Flat ES (armour category only) ---
  {
    id: 'prefix_flat_es',
    type: 'energyShield',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'local_es',
    allowedCategories: ['armour'],
    namePrefix: 'Parlak',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 11, valueMax: 18,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 19, valueMax: 28,  weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 29, valueMax: 40,  weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 41, valueMax: 55,  weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 56, valueMax: 72,  weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 73, valueMax: 90,  weight: 40 },
    ],
  },
  // --- Flat Damage (weapon only) ---
  {
    id: 'prefix_flat_damage',
    type: 'damage',
    calcType: 'flat',
    pool: 'prefix',
    modGroup: 'local_damage',
    allowedCategories: ['weapon'],
    namePrefix: 'Keskin',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 3,  valueMax: 6,   weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 7,  valueMax: 12,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 13, valueMax: 20,  weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 21, valueMax: 30,  weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 31, valueMax: 42,  weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 43, valueMax: 55,  weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 56, valueMax: 70,  weight: 40 },
    ],
  },
  // --- % Physical Damage (weapon only) ---
  {
    id: 'prefix_pct_phys',
    type: 'physicalDamage',
    calcType: 'increased',
    pool: 'prefix',
    modGroup: 'phys_dmg_pct',
    allowedCategories: ['weapon'],
    namePrefix: 'Vahşi',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 700 },
      { tier: 5, ilvlRequired: 12, valueMin: 11, valueMax: 18,  weight: 500 },
      { tier: 4, ilvlRequired: 20, valueMin: 19, valueMax: 28,  weight: 300 },
      { tier: 3, ilvlRequired: 26, valueMin: 29, valueMax: 40,  weight: 150 },
      { tier: 2, ilvlRequired: 32, valueMin: 41, valueMax: 55,  weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 56, valueMax: 75,  weight: 40 },
    ],
  },
  // --- % Elemental Damage (weapon only) ---
  {
    id: 'prefix_pct_ele',
    type: 'elementalDamage',
    calcType: 'increased',
    pool: 'prefix',
    modGroup: 'ele_dmg_pct',
    allowedCategories: ['weapon'],
    namePrefix: 'Elementli',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 600 },
      { tier: 5, ilvlRequired: 12, valueMin: 11, valueMax: 18,  weight: 450 },
      { tier: 4, ilvlRequired: 20, valueMin: 19, valueMax: 28,  weight: 250 },
      { tier: 3, ilvlRequired: 26, valueMin: 29, valueMax: 38,  weight: 120 },
      { tier: 2, ilvlRequired: 32, valueMin: 39, valueMax: 50,  weight: 60 },
      { tier: 1, ilvlRequired: 38, valueMin: 51, valueMax: 65,  weight: 30 },
    ],
  },
  // --- % Spell Damage (staff/wand + amulet) ---
  {
    id: 'prefix_pct_spell',
    type: 'spellDamage',
    calcType: 'increased',
    pool: 'prefix',
    modGroup: 'spell_dmg_pct',
    allowedSlots: ['weapon', 'amulet'],
    allowedWeaponTypes: ['staff', 'wand'],
    namePrefix: 'Büyücü',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 600 },
      { tier: 5, ilvlRequired: 12, valueMin: 11, valueMax: 18,  weight: 450 },
      { tier: 4, ilvlRequired: 20, valueMin: 19, valueMax: 28,  weight: 250 },
      { tier: 3, ilvlRequired: 26, valueMin: 29, valueMax: 40,  weight: 120 },
      { tier: 2, ilvlRequired: 32, valueMin: 41, valueMax: 55,  weight: 60 },
      { tier: 1, ilvlRequired: 38, valueMin: 56, valueMax: 75,  weight: 30 },
    ],
  },
];

// ========================
// SUFFIX MODS
// ========================

const SUFFIX_MOD_DEFINITIONS: ModDefinition[] = [
  // --- Strength (all slots) ---
  {
    id: 'suffix_str',
    type: 'strength',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'str',
    nameSuffix: 'Gücün',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 2,  valueMax: 4,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 5,  valueMax: 7,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 8,  valueMax: 10, weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 11, valueMax: 14, weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 15, valueMax: 18, weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 19, valueMax: 22, weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 23, valueMax: 28, weight: 40 },
    ],
  },
  // --- Agility (all slots) ---
  {
    id: 'suffix_agi',
    type: 'agility',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'agi',
    nameSuffix: 'Çevikliğin',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 2,  valueMax: 4,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 5,  valueMax: 7,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 8,  valueMax: 10, weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 11, valueMax: 14, weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 15, valueMax: 18, weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 19, valueMax: 22, weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 23, valueMax: 28, weight: 40 },
    ],
  },
  // --- Spirit (all slots) ---
  {
    id: 'suffix_spr',
    type: 'spirit',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'spr',
    nameSuffix: 'Ruhun',
    tiers: [
      { tier: 7, ilvlRequired: 1,  valueMin: 2,  valueMax: 4,  weight: 900 },
      { tier: 6, ilvlRequired: 8,  valueMin: 5,  valueMax: 7,  weight: 700 },
      { tier: 5, ilvlRequired: 15, valueMin: 8,  valueMax: 10, weight: 500 },
      { tier: 4, ilvlRequired: 22, valueMin: 11, valueMax: 14, weight: 300 },
      { tier: 3, ilvlRequired: 28, valueMin: 15, valueMax: 18, weight: 150 },
      { tier: 2, ilvlRequired: 33, valueMin: 19, valueMax: 22, weight: 80 },
      { tier: 1, ilvlRequired: 38, valueMin: 23, valueMax: 28, weight: 40 },
    ],
  },
  // --- Od Resist (armour + accessory) ---
  {
    id: 'suffix_od_resist',
    type: 'odResist',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'od_resist',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Od Kalkanı',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 6,  valueMax: 10,  weight: 700 },
      { tier: 5, ilvlRequired: 10, valueMin: 11, valueMax: 16,  weight: 500 },
      { tier: 4, ilvlRequired: 18, valueMin: 17, valueMax: 22,  weight: 300 },
      { tier: 3, ilvlRequired: 24, valueMin: 23, valueMax: 28,  weight: 150 },
      { tier: 2, ilvlRequired: 30, valueMin: 29, valueMax: 34,  weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 35, valueMax: 42,  weight: 40 },
    ],
  },
  // --- Su Resist (armour + accessory) ---
  {
    id: 'suffix_su_resist',
    type: 'suResist',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'su_resist',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Su Kalkanı',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 6,  valueMax: 10,  weight: 700 },
      { tier: 5, ilvlRequired: 10, valueMin: 11, valueMax: 16,  weight: 500 },
      { tier: 4, ilvlRequired: 18, valueMin: 17, valueMax: 22,  weight: 300 },
      { tier: 3, ilvlRequired: 24, valueMin: 23, valueMax: 28,  weight: 150 },
      { tier: 2, ilvlRequired: 30, valueMin: 29, valueMax: 34,  weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 35, valueMax: 42,  weight: 40 },
    ],
  },
  // --- Gök Resist (armour + accessory) ---
  {
    id: 'suffix_gok_resist',
    type: 'gokResist',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'gok_resist',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Gök Kalkanı',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 6,  valueMax: 10,  weight: 700 },
      { tier: 5, ilvlRequired: 10, valueMin: 11, valueMax: 16,  weight: 500 },
      { tier: 4, ilvlRequired: 18, valueMin: 17, valueMax: 22,  weight: 300 },
      { tier: 3, ilvlRequired: 24, valueMin: 23, valueMax: 28,  weight: 150 },
      { tier: 2, ilvlRequired: 30, valueMin: 29, valueMax: 34,  weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 35, valueMax: 42,  weight: 40 },
    ],
  },
  // --- Karanlık Resist (armour + accessory) - lower values due to 50% cap ---
  {
    id: 'suffix_karanlik_resist',
    type: 'karanlikResist',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'karanlik_resist',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Karanlık Kalkanı',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 4,  valueMax: 7,   weight: 500 },
      { tier: 5, ilvlRequired: 10, valueMin: 8,  valueMax: 11,  weight: 350 },
      { tier: 4, ilvlRequired: 18, valueMin: 12, valueMax: 16,  weight: 200 },
      { tier: 3, ilvlRequired: 24, valueMin: 17, valueMax: 21,  weight: 100 },
      { tier: 2, ilvlRequired: 30, valueMin: 22, valueMax: 27,  weight: 50 },
      { tier: 1, ilvlRequired: 36, valueMin: 28, valueMax: 35,  weight: 25 },
    ],
  },
  // --- All Resist (armour + accessory) ---
  {
    id: 'suffix_all_resist',
    type: 'allResist',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'all_resist',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Koruyucunun',
    tiers: [
      { tier: 5, ilvlRequired: 12, valueMin: 3,  valueMax: 5,  weight: 400 },
      { tier: 4, ilvlRequired: 20, valueMin: 6,  valueMax: 8,  weight: 200 },
      { tier: 3, ilvlRequired: 26, valueMin: 9,  valueMax: 11, weight: 100 },
      { tier: 2, ilvlRequired: 32, valueMin: 12, valueMax: 15, weight: 50 },
      { tier: 1, ilvlRequired: 38, valueMin: 16, valueMax: 20, weight: 25 },
    ],
  },
  // --- Crit Chance (weapon + gloves + amulet) ---
  {
    id: 'suffix_crit_chance',
    type: 'critChance',
    calcType: 'increased',
    pool: 'suffix',
    modGroup: 'crit_chance',
    allowedSlots: ['weapon', 'gloves', 'amulet'],
    nameSuffix: 'Öfkenin',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 8,   weight: 600 },
      { tier: 5, ilvlRequired: 10, valueMin: 9,  valueMax: 14,  weight: 450 },
      { tier: 4, ilvlRequired: 18, valueMin: 15, valueMax: 20,  weight: 250 },
      { tier: 3, ilvlRequired: 24, valueMin: 21, valueMax: 28,  weight: 120 },
      { tier: 2, ilvlRequired: 30, valueMin: 29, valueMax: 36,  weight: 60 },
      { tier: 1, ilvlRequired: 36, valueMin: 37, valueMax: 45,  weight: 30 },
    ],
  },
  // --- Crit Damage (weapon + amulet + ring) ---
  {
    id: 'suffix_crit_damage',
    type: 'critDamage',
    calcType: 'increased',
    pool: 'suffix',
    modGroup: 'crit_damage',
    allowedSlots: ['weapon', 'amulet', 'ring1', 'ring2'],
    nameSuffix: 'Yıkımın',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 5,  valueMax: 10,  weight: 600 },
      { tier: 5, ilvlRequired: 10, valueMin: 11, valueMax: 18,  weight: 450 },
      { tier: 4, ilvlRequired: 18, valueMin: 19, valueMax: 26,  weight: 250 },
      { tier: 3, ilvlRequired: 24, valueMin: 27, valueMax: 35,  weight: 120 },
      { tier: 2, ilvlRequired: 30, valueMin: 36, valueMax: 45,  weight: 60 },
      { tier: 1, ilvlRequired: 36, valueMin: 46, valueMax: 55,  weight: 30 },
    ],
  },
  // --- Attack Speed (weapon + gloves) ---
  {
    id: 'suffix_attack_speed',
    type: 'attackSpeed',
    calcType: 'increased',
    pool: 'suffix',
    modGroup: 'attack_speed',
    allowedSlots: ['weapon', 'gloves'],
    nameSuffix: 'Savaşçının',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 3,  valueMax: 5,  weight: 600 },
      { tier: 5, ilvlRequired: 10, valueMin: 6,  valueMax: 8,  weight: 450 },
      { tier: 4, ilvlRequired: 18, valueMin: 9,  valueMax: 12, weight: 250 },
      { tier: 3, ilvlRequired: 24, valueMin: 13, valueMax: 16, weight: 120 },
      { tier: 2, ilvlRequired: 30, valueMin: 17, valueMax: 20, weight: 60 },
      { tier: 1, ilvlRequired: 36, valueMin: 21, valueMax: 26, weight: 30 },
    ],
  },
  // --- Cast Speed (staff/wand + amulet) ---
  {
    id: 'suffix_cast_speed',
    type: 'castSpeed',
    calcType: 'increased',
    pool: 'suffix',
    modGroup: 'cast_speed',
    allowedSlots: ['weapon', 'amulet'],
    allowedWeaponTypes: ['staff', 'wand'],
    nameSuffix: 'Büyücünün',
    tiers: [
      { tier: 5, ilvlRequired: 1,  valueMin: 3,  valueMax: 5,  weight: 500 },
      { tier: 4, ilvlRequired: 12, valueMin: 6,  valueMax: 9,  weight: 350 },
      { tier: 3, ilvlRequired: 22, valueMin: 10, valueMax: 14, weight: 180 },
      { tier: 2, ilvlRequired: 30, valueMin: 15, valueMax: 19, weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 20, valueMax: 25, weight: 40 },
    ],
  },
  // --- Move Speed (boots ONLY) ---
  {
    id: 'suffix_move_speed',
    type: 'moveSpeed',
    calcType: 'increased',
    pool: 'suffix',
    modGroup: 'move_speed',
    allowedSlots: ['boots'],
    nameSuffix: 'Koşucunun',
    tiers: [
      { tier: 5, ilvlRequired: 1,  valueMin: 5,  valueMax: 8,  weight: 600 },
      { tier: 4, ilvlRequired: 12, valueMin: 9,  valueMax: 13, weight: 400 },
      { tier: 3, ilvlRequired: 22, valueMin: 14, valueMax: 18, weight: 200 },
      { tier: 2, ilvlRequired: 30, valueMin: 19, valueMax: 24, weight: 100 },
      { tier: 1, ilvlRequired: 36, valueMin: 25, valueMax: 30, weight: 50 },
    ],
  },
  // --- HP Regen (not weapon) ---
  {
    id: 'suffix_hp_regen',
    type: 'hpRegen',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'hp_regen',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Şifanın',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 1,  valueMax: 2,  weight: 700 },
      { tier: 5, ilvlRequired: 10, valueMin: 3,  valueMax: 4,  weight: 500 },
      { tier: 4, ilvlRequired: 18, valueMin: 5,  valueMax: 7,  weight: 300 },
      { tier: 3, ilvlRequired: 24, valueMin: 8,  valueMax: 10, weight: 150 },
      { tier: 2, ilvlRequired: 30, valueMin: 11, valueMax: 14, weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 15, valueMax: 20, weight: 40 },
    ],
  },
  // --- Mana Regen (not weapon) ---
  {
    id: 'suffix_mana_regen',
    type: 'manaRegen',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'mana_regen',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Akışın',
    tiers: [
      { tier: 6, ilvlRequired: 1,  valueMin: 1,  valueMax: 1,  weight: 700 },
      { tier: 5, ilvlRequired: 10, valueMin: 2,  valueMax: 3,  weight: 500 },
      { tier: 4, ilvlRequired: 18, valueMin: 4,  valueMax: 5,  weight: 300 },
      { tier: 3, ilvlRequired: 24, valueMin: 6,  valueMax: 8,  weight: 150 },
      { tier: 2, ilvlRequired: 30, valueMin: 9,  valueMax: 11, weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 12, valueMax: 15, weight: 40 },
    ],
  },
  // --- Life Leech (weapon + ring + amulet) ---
  {
    id: 'suffix_life_leech',
    type: 'lifeLeech',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'life_leech',
    allowedSlots: ['weapon', 'ring1', 'ring2', 'amulet'],
    nameSuffix: 'Vampirin',
    tiers: [
      { tier: 5, ilvlRequired: 10, valueMin: 0.3, valueMax: 0.5, weight: 400 },
      { tier: 4, ilvlRequired: 18, valueMin: 0.6, valueMax: 0.8, weight: 250 },
      { tier: 3, ilvlRequired: 24, valueMin: 0.9, valueMax: 1.2, weight: 120 },
      { tier: 2, ilvlRequired: 30, valueMin: 1.3, valueMax: 1.6, weight: 60 },
      { tier: 1, ilvlRequired: 36, valueMin: 1.7, valueMax: 2.0, weight: 30 },
    ],
  },
  // --- Block Chance (offhand/shield ONLY) ---
  {
    id: 'suffix_block_chance',
    type: 'blockChance',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'block',
    allowedSlots: ['offhand'],
    nameSuffix: 'Kalkandarın',
    tiers: [
      { tier: 5, ilvlRequired: 1,  valueMin: 2,  valueMax: 3,  weight: 500 },
      { tier: 4, ilvlRequired: 12, valueMin: 4,  valueMax: 5,  weight: 350 },
      { tier: 3, ilvlRequired: 22, valueMin: 6,  valueMax: 8,  weight: 180 },
      { tier: 2, ilvlRequired: 30, valueMin: 9,  valueMax: 11, weight: 80 },
      { tier: 1, ilvlRequired: 36, valueMin: 12, valueMax: 15, weight: 40 },
    ],
  },
  // --- Mana Leech (weapon + ring + amulet) ---
  {
    id: 'suffix_mana_leech',
    type: 'manaLeech',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'mana_leech',
    allowedSlots: ['weapon', 'ring1', 'ring2', 'amulet'],
    nameSuffix: 'Ruh Emenin',
    tiers: [
      { tier: 5, ilvlRequired: 10, valueMin: 0.2, valueMax: 0.4, weight: 350 },
      { tier: 4, ilvlRequired: 18, valueMin: 0.5, valueMax: 0.7, weight: 200 },
      { tier: 3, ilvlRequired: 24, valueMin: 0.8, valueMax: 1.0, weight: 100 },
      { tier: 2, ilvlRequired: 30, valueMin: 1.1, valueMax: 1.4, weight: 50 },
      { tier: 1, ilvlRequired: 36, valueMin: 1.5, valueMax: 1.8, weight: 25 },
    ],
  },
  // --- Cooldown Reduction (armour + accessory) ---
  {
    id: 'suffix_cdr',
    type: 'cooldownReduction',
    calcType: 'flat',
    pool: 'suffix',
    modGroup: 'cdr',
    allowedCategories: ['armour', 'accessory'],
    nameSuffix: 'Hazırlığın',
    tiers: [
      { tier: 5, ilvlRequired: 12, valueMin: 2,  valueMax: 3,  weight: 350 },
      { tier: 4, ilvlRequired: 20, valueMin: 4,  valueMax: 5,  weight: 200 },
      { tier: 3, ilvlRequired: 26, valueMin: 6,  valueMax: 8,  weight: 100 },
      { tier: 2, ilvlRequired: 32, valueMin: 9,  valueMax: 11, weight: 50 },
      { tier: 1, ilvlRequired: 38, valueMin: 12, valueMax: 15, weight: 25 },
    ],
  },
];

// ========================
// All Definitions Combined
// ========================

export const ALL_MOD_DEFINITIONS: ModDefinition[] = [
  ...PREFIX_MOD_DEFINITIONS,
  ...SUFFIX_MOD_DEFINITIONS,
];

// ========================
// Core Functions
// ========================

/**
 * Check if a mod definition can apply to a given item.
 */
function canModApplyToItem(mod: ModDefinition, item: EquipmentItem): boolean {
  const slot = item.slot;
  const category = item.category || 'armour';
  const weaponSubType = item.weaponSubType;

  // Check slot restrictions
  if (mod.allowedSlots) {
    // For weapon slot with weapon type restriction
    if (slot === 'weapon' && mod.allowedWeaponTypes) {
      if (!mod.allowedSlots.includes('weapon')) return false;
      if (weaponSubType && !mod.allowedWeaponTypes.includes(weaponSubType)) return false;
    } else {
      // For ring slots, check both ring1 and ring2
      const normalizedSlot = slot === 'ring2' ? 'ring1' : slot;
      if (!mod.allowedSlots.includes(normalizedSlot) && !mod.allowedSlots.includes(slot)) return false;
    }
  }

  // Check category restrictions
  if (mod.allowedCategories) {
    if (!mod.allowedCategories.includes(category)) return false;
  }

  // Check weapon-type only restriction (when no slot restriction but has weapon type)
  if (mod.allowedWeaponTypes && !mod.allowedSlots) {
    if (category !== 'weapon') return false;
    if (weaponSubType && !mod.allowedWeaponTypes.includes(weaponSubType)) return false;
  }

  return true;
}

/**
 * Get available tiers for a mod at a given ilvl.
 */
function getAvailableTiers(mod: ModDefinition, ilvl: number): ModTierEntry[] {
  return mod.tiers.filter(t => t.ilvlRequired <= ilvl);
}

/**
 * Roll a random value within a tier's range.
 */
function rollTierValue(tier: ModTierEntry): number {
  const range = tier.valueMax - tier.valueMin;
  // For floating point values (like life leech), keep one decimal
  if (tier.valueMin % 1 !== 0 || tier.valueMax % 1 !== 0) {
    return Math.round((tier.valueMin + Math.random() * range) * 10) / 10;
  }
  return Math.floor(tier.valueMin + Math.random() * (range + 1));
}

/**
 * Get the list of available mod definitions for an item,
 * filtered by pool (prefix/suffix), slot, category, weapon type, and existing mods.
 */
export function getAvailableMods(
  item: EquipmentItem,
  pool: 'prefix' | 'suffix',
  existingModGroups: Set<string>,
  ilvl?: number
): { mod: ModDefinition; tier: ModTierEntry }[] {
  const itemIlvl = ilvl ?? item.ilvl ?? item.level;
  const results: { mod: ModDefinition; tier: ModTierEntry }[] = [];

  for (const mod of ALL_MOD_DEFINITIONS) {
    if (mod.pool !== pool) continue;
    if (!canModApplyToItem(mod, item)) continue;

    // Skip if same mod group already present
    if (mod.modGroup && existingModGroups.has(mod.modGroup)) continue;

    // Get available tiers for this ilvl
    const tiers = getAvailableTiers(mod, itemIlvl);
    if (tiers.length === 0) continue;

    // Pick one tier (weighted)
    for (const tier of tiers) {
      results.push({ mod, tier });
    }
  }

  return results;
}

/**
 * Roll a single mod for an item from the available pool.
 * Returns null if no mods are available.
 */
export function rollSingleMod(
  item: EquipmentItem,
  pool: 'prefix' | 'suffix',
  existingModGroups: Set<string>,
  ilvl?: number
): ItemMod | null {
  const available = getAvailableMods(item, pool, existingModGroups, ilvl);
  if (available.length === 0) return null;

  // Weighted selection
  const totalWeight = available.reduce((sum, a) => sum + a.tier.weight, 0);
  let roll = Math.random() * totalWeight;

  for (const entry of available) {
    roll -= entry.tier.weight;
    if (roll <= 0) {
      const value = rollTierValue(entry.tier);
      return {
        type: entry.mod.type,
        value,
        tier: entry.tier.tier,
        calcType: entry.mod.calcType === 'flat' ? undefined : entry.mod.calcType,
      };
    }
  }

  // Fallback (shouldn't reach here)
  const last = available[available.length - 1];
  return {
    type: last.mod.type,
    value: rollTierValue(last.tier),
    tier: last.tier.tier,
    calcType: last.mod.calcType === 'flat' ? undefined : last.mod.calcType,
  };
}

/**
 * Collect mod groups from existing mods on an item.
 */
export function collectModGroups(item: EquipmentItem): Set<string> {
  const groups = new Set<string>();
  const allMods = [...(item.prefixes || []), ...(item.suffixes || [])];

  for (const mod of allMods) {
    // Find the definition by type + calcType match
    for (const def of ALL_MOD_DEFINITIONS) {
      if (def.type === mod.type && (def.calcType === (mod.calcType || 'flat'))) {
        if (def.modGroup) groups.add(def.modGroup);
        break;
      }
    }
  }

  return groups;
}

/**
 * Roll all mods for a newly created item based on rarity.
 * This is the main function called during item creation.
 */
export function rollModsForItem(
  item: EquipmentItem,
  prefixCount: number,
  suffixCount: number,
  ilvl?: number
): { prefixes: ItemMod[]; suffixes: ItemMod[] } {
  const usedGroups = new Set<string>();
  const prefixes: ItemMod[] = [];
  const suffixes: ItemMod[] = [];

  // Roll prefixes
  for (let i = 0; i < prefixCount; i++) {
    const mod = rollSingleMod(item, 'prefix', usedGroups, ilvl);
    if (mod) {
      prefixes.push(mod);
      // Add mod group to used set
      for (const def of ALL_MOD_DEFINITIONS) {
        if (def.type === mod.type && def.calcType === (mod.calcType || 'flat') && def.modGroup) {
          usedGroups.add(def.modGroup);
          break;
        }
      }
    }
  }

  // Roll suffixes
  for (let i = 0; i < suffixCount; i++) {
    const mod = rollSingleMod(item, 'suffix', usedGroups, ilvl);
    if (mod) {
      suffixes.push(mod);
      for (const def of ALL_MOD_DEFINITIONS) {
        if (def.type === mod.type && def.calcType === (mod.calcType || 'flat') && def.modGroup) {
          usedGroups.add(def.modGroup);
          break;
        }
      }
    }
  }

  return { prefixes, suffixes };
}

/**
 * Get the ModDefinition for a given mod type and calcType.
 * Useful for tooltip display.
 */
export function getModDefinition(type: string, calcType?: ModifierCalcType): ModDefinition | undefined {
  const ct = calcType || 'flat';
  return ALL_MOD_DEFINITIONS.find(d => d.type === type && d.calcType === ct);
}

/**
 * Get the tier entry for a mod based on its value.
 * Used to determine which tier a rolled value corresponds to.
 */
export function getTierForValue(type: string, calcType: ModifierCalcType | undefined, value: number): ModTierEntry | undefined {
  const def = getModDefinition(type, calcType);
  if (!def) return undefined;

  // Find the tier whose range includes this value
  for (const tier of def.tiers) {
    if (value >= tier.valueMin && value <= tier.valueMax) {
      return tier;
    }
  }

  // If not found, find the closest tier
  let closest: ModTierEntry | undefined;
  let closestDist = Infinity;
  for (const tier of def.tiers) {
    const mid = (tier.valueMin + tier.valueMax) / 2;
    const dist = Math.abs(value - mid);
    if (dist < closestDist) {
      closestDist = dist;
      closest = tier;
    }
  }

  return closest;
}

// ========================
// Name Generation Helpers
// ========================

// Fallback name maps for mod types not yet in ModifierPool definitions
const FALLBACK_PREFIXES: Record<string, string> = {
  maxHp: 'Canlı',
  maxMana: 'Ruhani',
  armor: 'Zırhlı',
  damage: 'Keskin',
  energyShield: 'Parlak',
  evasion: 'Çevik',
  physicalDamage: 'Vahşi',
  elementalDamage: 'Elementli',
  spellDamage: 'Büyücü',
  odDamage: 'Alevli',
  suDamage: 'Buzlu',
  gokDamage: 'Şimşekli',
  karanlikDamage: 'Karanlık',
};

const FALLBACK_SUFFIXES: Record<string, string> = {
  strength: 'Gücün',
  agility: 'Çevikliğin',
  spirit: 'Ruhun',
  critChance: 'Öfkenin',
  critDamage: 'Yıkımın',
  moveSpeed: 'Koşucunun',
  attackSpeed: 'Savaşçının',
  castSpeed: 'Büyücünün',
  cooldownReduction: 'Hazırlığın',
  odResist: 'Od Kalkanı',
  suResist: 'Su Kalkanı',
  gokResist: 'Gök Kalkanı',
  karanlikResist: 'Karanlık Kalkanı',
  allResist: 'Koruyucunun',
  hpRegen: 'Şifanın',
  manaRegen: 'Akışın',
  lifeLeech: 'Vampirin',
  manaLeech: 'Ruh Emenin',
  blockChance: 'Kalkandarın',
  evasion: 'Kaçışın',
};

/**
 * Get the Turkish name prefix for a mod type.
 * Checks ModDefinition.namePrefix first, then falls back to static map.
 */
export function getModNamePrefix(type: string, calcType?: ModifierCalcType): string {
  const def = ALL_MOD_DEFINITIONS.find(d => d.type === type && d.pool === 'prefix' && d.calcType === (calcType || 'flat'));
  if (def?.namePrefix) return def.namePrefix;
  return FALLBACK_PREFIXES[type] || '';
}

/**
 * Get the Turkish name suffix for a mod type.
 * Checks ModDefinition.nameSuffix first, then falls back to static map.
 */
export function getModNameSuffix(type: string, calcType?: ModifierCalcType): string {
  const def = ALL_MOD_DEFINITIONS.find(d => d.type === type && d.pool === 'suffix' && d.calcType === (calcType || 'flat'));
  if (def?.nameSuffix) return def.nameSuffix;
  return FALLBACK_SUFFIXES[type] || '';
}
